import { ChangelogEntry } from '../changelog';
import { useI18n } from '../i18n/I18nProvider';

export interface ChangelogModalProps {
  open: boolean;
  currentVersion: string;
  entries: ChangelogEntry[];
  onClose(): void;
}

/**
 * Close is deliberate: the X button and the footer button only. Backdrop clicks
 * and Escape must not dismiss the modal (frontend-modal-policy).
 */
export function ChangelogModal({
  open,
  currentVersion,
  entries,
  onClose,
}: ChangelogModalProps): JSX.Element | null {
  const { t } = useI18n();
  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" role="presentation" data-testid="changelog-backdrop">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="changelog-title"
      >
        <header className="modal__head">
          <h2 id="changelog-title">{t('changelogTitle')}</h2>
          <button
            type="button"
            className="modal__x"
            onClick={onClose}
            aria-label={t('changelogClose')}
          >
            {'×'}
          </button>
        </header>
        <div className="modal__body" data-testid="changelog-body">
          {entries.map((entry) => (
            <section key={entry.version} className="changelog-entry">
              <h3 className="changelog-entry__title">
                {`v${entry.version} · ${entry.date}`}
                {entry.version === currentVersion ? (
                  <span className="badge badge--current">{t('changelogCurrent')}</span>
                ) : null}
              </h3>
              {entry.sections.length === 0 ? (
                <p className="muted">{t('changelogEmpty')}</p>
              ) : (
                entry.sections.map((section) => (
                  <div key={`${entry.version}-${section.title}`} className="changelog-section">
                    <h4>{section.title}</h4>
                    <ul>
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </section>
          ))}
        </div>
        <footer className="modal__foot">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            {t('changelogClose')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export interface ChangelogModalState {
  open: boolean;
}

export type ChangelogModalAction = { type: 'open' } | { type: 'close' };

export function changelogModalReducer(
  state: ChangelogModalState,
  action: ChangelogModalAction,
): ChangelogModalState {
  switch (action.type) {
    case 'open':
      return { open: true };
    case 'close':
      return { open: false };
    default:
      return state;
  }
}
